/**
 * Init Splide générique pour les sections "max-*".
 * Chargé en <script defer> APRÈS splide.min.js (UMD global `Splide`).
 * Options lues depuis les data-* de l'élément .max-splide :
 *   data-loop            "true" -> boucle infinie (type loop), sinon slide
 *   data-arrows          "true" -> flèches
 *   data-dots            "true" -> pagination (dots)
 *   data-per-page        nb de slides visibles (ordinateur), défaut 1
 *   data-per-page-mobile nb de slides visibles (<=749px), défaut 1
 *   data-gap             espace entre slides, ex "12px"
 */
(function () {
  // Garde d'idempotence : ce script est un script CLASSIQUE (pas un module), donc il est
  // RÉ-EXÉCUTÉ à chaque balise <script> qui le charge, et aussi quand la Section Rendering
  // API ré-injecte du HTML contenant ses balises. Sans ce garde, chaque exécution créait un
  // MutationObserver de plus sur tout le DOM (childList + subtree) -> coût permanent énorme.
  // On ne veut qu'UNE seule initialisation par page.
  if (window.__maxSplideInit) return;
  window.__maxSplideInit = true;

  function buildOptions(el) {
    var perPage = parseInt(el.dataset.perPage || '1', 10);
    var perPageMobile = parseInt(el.dataset.perPageMobile || '1', 10);

    var base = {
      type: el.dataset.fade === 'true' ? 'fade' : el.dataset.loop === 'true' ? 'loop' : 'slide',
      perMove: 1,
      gap: el.dataset.gap || '0px',
      arrows: el.dataset.arrows === 'true',
      pagination: el.dataset.dots === 'true',
      drag: el.dataset.fade !== 'true',
      keyboard: true,
      speed: 500,
      // data-rewind="true" : reboucle au début après le dernier slide (utile en mode fade qui ne "loop" pas)
      rewind: el.dataset.rewind === 'true',
      omitEnd: true,
      // Autoplay (ex. barre d'annonce) : data-autoplay="true" + data-interval="5000"
      autoplay: el.dataset.autoplay === 'true',
      interval: parseInt(el.dataset.interval || '5000', 10),
      // data-no-pause="true" : ne pas mettre en pause au survol / focus (ex. barre d'annonce dans un petit drawer)
      pauseOnHover: el.dataset.noPause !== 'true',
      pauseOnFocus: el.dataset.noPause !== 'true',
    };

    // Mode "grille sur ordinateur, carrousel sur mobile" :
    // Splide est actif en mobile et se DÉTRUIT à partir de 750px (la grille CSS reprend la main).
    if (el.dataset.desktopGrid === 'true') {
      base.perPage = perPageMobile;
      base.mediaQuery = 'min';
      base.breakpoints = { 750: { destroy: true } };
      return base;
    }

    base.perPage = perPage;
    base.mediaQuery = 'max';
    base.breakpoints = { 749: { perPage: perPageMobile } };
    return base;
  }

  function mount(el) {
    if (!el || el.dataset.splideMounted === '1') return;
    if (typeof window.Splide === 'undefined') return;

    el.dataset.splideMounted = '1';
    var splide = new window.Splide(el, buildOptions(el)).mount();

    // En mode "grille desktop / carrousel mobile", Splide gère lui-même le montage
    // et la destruction selon la largeur -> pas de ResizeObserver.
    if (el.dataset.desktopGrid === 'true') return;

    // Conteneur parfois masqué au montage (ex. slider mobile-only en display:none) :
    // Splide se mesure alors à une largeur de 0 et les slides débordent.
    // On le rafraîchit dès qu'il passe de invisible (largeur 0) à visible.
    if (typeof ResizeObserver !== 'undefined') {
      var wasZero = el.getBoundingClientRect().width === 0;
      var ro = new ResizeObserver(function () {
        var w = el.getBoundingClientRect().width;
        if (w === 0) {
          wasZero = true;
        } else if (wasZero) {
          wasZero = false;
          splide.refresh();
          // Autoplay monté alors que le conteneur était caché (ex. drawer fermé) :
          // on le relance maintenant que c'est visible.
          if (el.dataset.autoplay === 'true' && splide.Components && splide.Components.Autoplay) {
            splide.Components.Autoplay.play();
          }
        }
      });
      ro.observe(el);
    }
  }

  /**
   * Montage PARESSEUX : on n'initialise Splide qu'à l'approche de l'écran.
   *
   * Pourquoi : le montage de Splide LIT la géométrie de chaque slide puis ÉCRIT sa largeur.
   * Sur la home, 5 sliders (3 onglets + 2 « collection accueil »), tous SOUS le hero plein
   * écran, se montaient d'un coup au chargement -> 5 handlers `mounted` de ~350-475ms + un
   * « Forced reflow » de ~3s, alors qu'aucun n'est visible. En différant chaque montage
   * jusqu'à ce que le slider approche du viewport, tout ce travail sort du chargement
   * initial et se répartit au fil du défilement.
   *
   * rootMargin 300px : on monte un peu AVANT que le slider n'entre à l'écran, pour qu'il
   * soit prêt quand on l'atteint (pas de pop). La mise en page pré-init (CSS) l'affiche
   * correctement en attendant.
   */
  var io = null;
  if (typeof IntersectionObserver !== 'undefined') {
    io = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (!entries[i].isIntersecting) continue;
          var el = entries[i].target;
          io.unobserve(el);
          mount(el);
        }
      },
      { rootMargin: '300px 0px' }
    );
  }

  function lazyMount(el) {
    if (el.dataset.splideMounted === '1') return;
    // Pas d'IntersectionObserver (vieux navigateur) -> montage immédiat, comme avant.
    if (io) io.observe(el);
    else mount(el);
  }

  /**
   * Slider mobile-only (data-mobile-only="true") : on ne monte Splide QUE
   * lorsque le viewport est réellement mobile, pour éviter un montage dans
   * un conteneur display:none (largeur 0 -> slides qui débordent).
   * Le montage lui-même reste paresseux (déclenché à l'approche de l'écran).
   */
  function setup(el) {
    if (!el) return;

    if (el.dataset.mobileOnly === 'true') {
      var mql = window.matchMedia('(max-width: 749px)');
      var attempt = function () {
        if (mql.matches) lazyMount(el);
      };
      attempt();
      if (mql.addEventListener) mql.addEventListener('change', attempt);
      else if (mql.addListener) mql.addListener(attempt);
    } else {
      lazyMount(el);
    }
  }

  function mountAll(scope) {
    var root = scope && scope.querySelectorAll ? scope : document;
    root.querySelectorAll('.max-splide').forEach(setup);
  }

  // Exposé pour le contenu injecté après le chargement, en dehors des cas déjà gérés plus bas
  // (éditeur de thème, re-render du cart-drawer) : voir max-collection-tabs.js, qui sort les
  // onglets inactifs d'un <template> à leur première activation.
  window.maxSplideMount = mountAll;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      mountAll();
    });
  } else {
    mountAll();
  }

  // Éditeur de thème : re-mount quand une section est rechargée
  document.addEventListener('shopify:section:load', function (event) {
    mountAll(event.target);
  });

  // Re-render AJAX du panier : le cart-drawer remplace son contenu (nouvel upsell / barre
  // d'annonce = nouveaux .max-splide non montés). On observe UNIQUEMENT <cart-drawer-component>,
  // jamais tout le document : ce nœud ne mute qu'au changement de panier, pas au scroll ni sous
  // l'effet des apps (Klaviyo, Instafeed…). Fini le coût permanent sur toute la page.
  var drawer = document.querySelector('cart-drawer-component');
  if (drawer && typeof MutationObserver !== 'undefined') {
    new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if ((node.matches && node.matches('.max-splide')) ||
              (node.querySelector && node.querySelector('.max-splide'))) {
            mountAll(drawer);
            return;
          }
        }
      }
    }).observe(drawer, { childList: true, subtree: true });
  }
})();
